import React, { forwardRef } from 'react';
import { Box, Stack, Typography, FormHelperText } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EditionFormDataType } from '../types';

export const MAX_UPLOAD_SIZE = 10000000; // 10MB

interface FormDocumentDisplayProps {
  name: keyof EditionFormDataType;
  control: Control<EditionFormDataType>;
  onFileChange: (file: File | null) => void;
}

const FormDocumentDisplay = forwardRef<{ open: () => void }, FormDocumentDisplayProps>(
  ({ control, name, onFileChange }, ref) => {
    const { t } = useTranslation();

    const handleDrop = (acceptedFiles: File[]) => {
      const document = acceptedFiles?.[0] ?? null;
      onFileChange(document);
    };

    const { getRootProps, getInputProps, isDragReject, fileRejections, open } = useDropzone({
      onDrop: handleDrop,
      multiple: false,
      accept: {
        'application/pdf': ['.pdf'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-excel': ['.xls'],
      },
      minSize: 0,
      maxSize: MAX_UPLOAD_SIZE,
    });

    React.useImperativeHandle(ref, () => ({
      open,
    }), [open]);

    const isFileTooLarge = React.useMemo(
      () => fileRejections.length > 0 && fileRejections[0].file.size > MAX_UPLOAD_SIZE,
      [fileRejections],
    );

    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value, ...restField } }) => {
          const inputProps = getInputProps();
          return (
            <Box
              {...getRootProps()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '250px',
                border: 'dotted 2px #DDD',
                borderRadius: '8px',
              }}
            >
              <input
                {...inputProps}
                {...restField}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  inputProps.onChange?.(e);
                  const newFile = e.target.files?.[0] || null;
                  onChange(newFile);
                  onFileChange(newFile);
                }}
              />
              <Stack
                gap={1}
                alignItems="center"
                justifyContent="center"
                color="#DADDE3"
              >
                <Box>
                  <i style={{ fontSize: '32px' }} className="fa-solid fa-cloud-arrow-up"></i>
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    lineHeight: '21px',
                    color: 'content.secondary',
                    fontWeight: '500',
                  }}
                >
                  {t('documents.edition.content.main_drop_text')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: 'content.tertiary',
                    fontWeight: '400',
                    textAlign: 'center',
                  }}
                >
                  <p>{t('documents.edition.content.secondary_drop_text')}</p>
                  <Stack alignItems="center">
                    {isFileTooLarge && (
                      <FormHelperText error>
                        {t('documents.edition.validation.file_too_large')}
                      </FormHelperText>
                    )}
                    {isDragReject && (
                      <FormHelperText error>
                        {t('documents.edition.validation.drop_error')}
                      </FormHelperText>
                    )}
                    {fileRejections.map(({ errors }) =>
                      errors.map((e) => <FormHelperText error key={e.code}>{`Erreur lors de l'import: ${e.code}`}</FormHelperText>),
                    )}
                  </Stack>
                </Typography>

                {value && typeof value === 'object' && 'name' in value ? (
                  <Typography>
                    {t('documents.edition.content.document.selected_file')}: {value.name}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        }}
      />
    );
  }
);

export default FormDocumentDisplay;